import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Modal, Popconfirm, Popover, Tree } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { EmptyState } from '../empty_state/EmptyState';
import AddHierarchyEntityPopup from './AddHierarchyEntityPopup';
import EditHierarchyEntityPopup from './EditHierarchyEntityPopup';

interface OWSimpleHierarchyEditProps {
	sourceData: any[];
	allowedToEdit: boolean;
	entityName: string;
	requiredParams?: any;
	canEditTitle: (node: any) => boolean;
	canDelete: (node: any) => boolean;
	createEntity: (entity: any) => any;
	updateEntity: (entity: any) => any;
	deleteEntity: (entity: any) => any;
	getChildrenCount: (entity: any) => any;
	refreshEntities: () => any;
	renderTitle?: (node: any) => React.ReactElement;
}

const addKeys = (data) =>
	data &&
	data.map((obj) => ({
		...obj,
		key: data.id,
		children: addKeys(obj.children),
	}));

const OWSimpleHierarchyEdit: FC<OWSimpleHierarchyEditProps> = ({
	sourceData,
	allowedToEdit,
	entityName,
	requiredParams = {},
	canEditTitle,
	canDelete,
	createEntity,
	updateEntity,
	deleteEntity,
	getChildrenCount,
	refreshEntities,
	renderTitle = (node) => node.name,
}): React.ReactElement => {
	const [currentParent, setCurrentParent] = useState(null);
	const [addEntityVisible, setAddEntityVisible] = useState(false);
	const [editingEntity, setEditingEntity] = useState(null);
	const [editEntityVisible, setEditEntityVisible] = useState(false);
	const [fetchingCountForEntityId, setFetchingCountForEntityId] = useState(null);
	const [deletingEntity, setDeletingEntity] = useState(null);

	const showEditEntity = useCallback(
		(entity) => () => {
			setEditingEntity(entity);
			setEditEntityVisible(true);
		},
		[]
	);

	const getCount = useCallback(
		(entity) => () => {
			setFetchingCountForEntityId(entity.id);
			setCurrentParent(entity);
			getChildrenCount(entity.id)
				.then((res) => {
					if (res) {
						Modal.error({
							title: `Cannot add ${entityName} to '${nullSafeGetOrElse(
								'name',
								entity,
								`this ${entityName}`
							)}'!`,
							content: `${
								res > 1 ? `There are ${res} locations` : `There is a location`
							} associated with '${nullSafeGetOrElse(
								'name',
								entity,
								`this ${entityName}`
							)}'. Please remove association to proceed.`,
						});
					} else {
						setAddEntityVisible(true);
					}
				})
				.finally(() => setFetchingCountForEntityId(null));
		},
		[entityName, getChildrenCount]
	);

	const onDeleteEntity = useCallback(
		(entity) => () => {
			setDeletingEntity(entity.id);
			deleteEntity(entity)
				.then(() => {
					message.success(`Deleted successfully!`);
					refreshEntities();
				})
				.catch((err) => message.error(err))
				.finally(() => setDeletingEntity(null));
		},
		[deleteEntity, refreshEntities]
	);

	const isDeletingRecord = useCallback(
		(record) => deletingEntity === nullSafeGet('id', record),
		[deletingEntity]
	);

	const renderNode = useCallback(
		(nodeData) => (
			<div className="flex flex-row items-baseline pb-4">
				<div className="flex flex-row items-center">
					<div className="text-lg">{renderTitle(nodeData)}</div>
					{allowedToEdit ? (
						<>
							{canEditTitle(nodeData) ? (
								<Popover trigger="hover" content="Edit">
									<Button
										icon={<EditOutlined />}
										size="small"
										type="link"
										onClick={showEditEntity(nodeData)}
									/>
								</Popover>
							) : null}
							{canDelete(nodeData) ? (
								<Popconfirm
									title={`Are you sure you want to delete this ${entityName}?`}
									onConfirm={onDeleteEntity(nodeData)}
									okText="Delete"
									cancelText="Cancel"
								>
									<Popover trigger="hover" content="Delete">
										<Button
											icon={<DeleteOutlined />}
											size="small"
											type="link"
											loading={isDeletingRecord(nodeData)}
										/>
									</Popover>
								</Popconfirm>
							) : null}
							<Popover trigger="hover" content={`Add ${entityName}`}>
								<Button
									icon={<PlusOutlined />}
									type="link"
									size="small"
									loading={nodeData.id === fetchingCountForEntityId}
									onClick={getCount(nodeData)}
								/>
							</Popover>
						</>
					) : null}
				</div>
			</div>
		),
		[
			renderTitle,
			allowedToEdit,
			canEditTitle,
			showEditEntity,
			canDelete,
			entityName,
			onDeleteEntity,
			isDeletingRecord,
			fetchingCountForEntityId,
			getCount,
		]
	);

	const _hideAddEntity = () => setAddEntityVisible(false);
	const _hideEditEntity = () => setEditEntityVisible(false);

	const onEntityAddSuccess = useCallback(() => {
		_hideAddEntity();
		refreshEntities();
	}, [refreshEntities]);

	const onEntityEditSuccess = useCallback(() => {
		_hideEditEntity();
		refreshEntities();
	}, [refreshEntities]);

	return (
		<>
			{sourceData && sourceData.length > 0 ? (
				<Tree
					rootClassName="draggable-tree pt-4"
					defaultExpandAll
					showLine={{ showLeafIcon: false }}
					selectable={false}
					treeData={addKeys(sourceData)}
					titleRender={renderNode}
				/>
			) : (
				<EmptyState
					height={120}
					headline={`No ${entityName}s found`}
					body={
						<div style={{ maxWidth: 488, textAlign: 'center' }}>
							{`Currently there are no ${entityName}s available. Please add ${entityName} to view here.`}
						</div>
					}
				/>
			)}
			{addEntityVisible && (
				<AddHierarchyEntityPopup
					parentData={currentParent}
					entityName={entityName}
					requiredParams={requiredParams}
					onSuccess={onEntityAddSuccess}
					onCancel={_hideAddEntity}
					onSave={createEntity}
				/>
			)}
			{editEntityVisible && (
				<EditHierarchyEntityPopup
					entity={editingEntity}
					entityName={entityName}
					requiredParams={requiredParams}
					onSuccess={onEntityEditSuccess}
					onCancel={_hideAddEntity}
					onSave={updateEntity}
				/>
			)}
		</>
	);
};

export default OWSimpleHierarchyEdit;
